<template>
    <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
        <Header :title="$t('form_question.title')" />

        <div class="ppv2-flex ppv2-items-end ppv2-gap-1 ppv2-border-b-2" style="border-color: #e2e6ec" ref="tabMenuArea">
            <div
                v-for="tab in tabs"
                :key="tab.slug"
                class="ppv2-relative ppv2-flex ppv2-items-center ppv2-h-[44px] ppv2-gap-[9px] ppv2-pl-[14px] ppv2-pr-2 ppv2-rounded-t-[10px] ppv2-cursor-pointer ppv2-select-none ppv2-border-b-2 ppv2-mb-[-2px] ppv2-transition-colors hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10"
                :class="
                    activeSlug === tab.slug
                        ? 'ppv2-bg-white dark:ppv2-bg-dark ppv2-border-[#7d2dbd]'
                        : 'ppv2-bg-transparent ppv2-border-transparent hover:ppv2-border-[#b3bcc9]'
                "
                @click="selectTab(tab.slug)"
            >
                <span
                    class="ppv2-shrink-0 ppv2-w-[7px] ppv2-h-[7px] ppv2-rounded-full"
                    :style="{ background: isActive(tab.slug) ? '#22a06b' : '#cbd2dc' }"
                ></span>
                <input
                    v-if="renamingSlug === tab.slug"
                    v-model="renameInput"
                    ref="renameInput"
                    type="text"
                    class="ppv2-w-[150px] ppv2-h-7 ppv2-px-2 ppv2-rounded-[7px] ppv2-outline-none poppinsMedium"
                    style="border: 1px solid #ab6edc; color: #021832"
                    :style="{ fontSize: normalizeFontSize(13.5) }"
                    @click.stop
                    @keyup.enter="commitRename"
                    @blur="commitRename"
                />
                <span
                    v-else
                    class="poppinsMedium ppv2-whitespace-nowrap"
                    :class="activeSlug === tab.slug ? 'ppv2-text-black' : 'ppv2-text-[#6b7480]'"
                    :style="{ fontSize: normalizeFontSize(13.5) }"
                >
                    {{ formMeta(tab.slug).name || tab.fallback }}
                </span>
                <span
                    v-if="formMeta(tab.slug).loaded"
                    class="poppinsRegular ppv2-shrink-0 ppv2-bg-black ppv2-bg-opacity-10"
                    style="color: #9aa4b2; border-radius: 5px; padding: 2px 6px"
                    :style="{ fontSize: normalizeFontSize(11.5) }"
                >
                    {{ formMeta(tab.slug).count }}
                </span>
                <button
                    type="button"
                    :title="$t('forms.menu.options')"
                    class="ppv2-shrink-0 ppv2-w-6 ppv2-h-6 ppv2-rounded-[6px] ppv2-flex ppv2-items-center ppv2-justify-center hover:ppv2-bg-black hover:ppv2-bg-opacity-10 hover:ppv2-text-black ppv2-transition-colors"
                    :class="activeSlug === tab.slug ? 'ppv2-text-[#6b7480]' : 'ppv2-text-[#cbd2dc]'"
                    @click.stop="menuClick(tab.slug)"
                >
                    <MoreHorizontal :size="16" class="ppv2-text-black" />
                </button>

                <div
                    v-if="menuSlug === tab.slug"
                    class="menuDropdown ppv2-absolute ppv2-top-full ppv2-right-0 ppv2-mt-2 ppv2-rounded-md ppv2-overflow-hidden ppv2-z-50 ppv2-min-w-[220px] ppv2-shadow-lg"
                    @click.stop
                >
                    <button
                        type="button"
                        class="dropdownItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 ppv2-transition-colors"
                        :style="{ fontSize: normalizeFontSize(13) }"
                        @click="startRename(tab.slug)"
                    >
                        <span>{{ $t('forms.menu.rename') }}</span>
                    </button>
                    <button
                        v-if="formCount < maxForms"
                        type="button"
                        class="dropdownItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 ppv2-transition-colors"
                        :style="{ fontSize: normalizeFontSize(13) }"
                        @click="duplicateForm(tab.slug)"
                    >
                        <span>{{ $t('forms.menu.duplicate') }}</span>
                    </button>
                    <button
                        type="button"
                        class="dropdownItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 ppv2-transition-colors"
                        :style="{ fontSize: normalizeFontSize(13) }"
                        @click="toggleActive(tab.slug)"
                    >
                        <span>{{ isActive(tab.slug) ? $t('forms.menu.disable') : $t('forms.menu.enable') }}</span>
                    </button>
                    <button
                        type="button"
                        :disabled="!canDeleteDemandeDevis || formCount <= 1"
                        class="dropdownItem menu-item-danger hover:ppv2-bg-[#b3261e] hover:ppv2-bg-opacity-10 ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-transition-colors"
                        :class="
                            canDeleteDemandeDevis && formCount > 1
                                ? 'ppv2-text-gray-700 ppv2-cursor-pointer'
                                : 'ppv2-text-gray-400 ppv2-cursor-not-allowed'
                        "
                        :title="
                            canDeleteDemandeDevis && formCount > 1
                                ? ''
                                : formCount <= 1
                                  ? $t('forms.menu.cannotDeleteLast')
                                  : $t('forms.menu.soon')
                        "
                        :style="{ fontSize: normalizeFontSize(13) }"
                        @click="canDeleteDemandeDevis && formCount > 1 && requestDeleteForm(tab.slug)"
                    >
                        <span>{{ $t('forms.menu.delete') }}</span>
                    </button>
                </div>
            </div>

            <div style="width: 1px; height: 24px; background: #e2e6ec; margin: 0 8px 10px 8px"></div>

            <button
                type="button"
                :disabled="formCount >= maxForms"
                :title="formCount >= maxForms ? $t('forms.maxForms') : ''"
                class="ppv2-h-8 ppv2-mb-[6px] ppv2-px-3 ppv2-rounded-lg ppv2-border ppv2-border-dashed ppv2-bg-white dark:ppv2-bg-dark ppv2-flex ppv2-items-center ppv2-gap-1.5 poppinsRegular hover:ppv2-border-[#7d2dbd] hover:ppv2-text-[#7d2dbd] ppv2-transition-colors"
                :class="formCount >= maxForms ? 'ppv2-cursor-not-allowed' : 'ppv2-cursor-pointer'"
                :style="[
                    { fontSize: normalizeFontSize(13) },
                    formCount >= maxForms
                        ? { borderColor: '#e2e6ec', color: '#cbd2dc' }
                        : { borderColor: '#cbd2dc', color: '#6b7480' },
                ]"
                @click="addForm"
            >
                <FormTypeIcon name="plus" :size="15" />
                {{ $t('forms.addNewLimit', { count: formCount, max: maxForms }) }}
            </button>
        </div>

        <div class="ppv2-mt-4">
            <div
                class="ppv2-inline-flex ppv2-items-center ppv2-gap-2.5 ppv2-px-3 ppv2-py-[7px] ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-[#e2e6ec] hover:ppv2-border-[#cbd2dc] ppv2-rounded-[9px] ppv2-cursor-pointer ppv2-transition-colors"
                @click="toggleActive(activeSlug)"
            >
                <div @click.stop>
                    <BaseSwitch :model-value="isActive(activeSlug)" variant="success" @input="toggleActive(activeSlug)" />
                </div>
                <span class="poppinsRegular ppv2-text-black ppv2-text-opacity-70" :style="{ fontSize: normalizeFontSize(13) }">
                    {{ isActive(activeSlug) ? $t('forms.activeOnSite') : $t('forms.inactiveOnSite') }}
                </span>
            </div>
        </div>

        <div v-if="activeSlug" class="ppv2-mt-6">
            <FormBuilder
                :slug="activeSlug"
                :form-name="activeForm.name || activeFallbackName"
                :token="token"
                @saved="onSaved"
                @count-change="onCountChange"
            />
        </div>

        <div
            v-if="deleteCandidateSlug"
            class="ppv2-fixed ppv2-inset-0 ppv2-z-[100] ppv2-flex ppv2-items-center ppv2-justify-center"
        >
            <div class="ppv2-absolute ppv2-inset-0 ppv2-bg-black/40" @click="deleteCandidateSlug = null"></div>
            <div class="ppv2-relative ppv2-w-[420px] ppv2-max-w-[90vw] ppv2-bg-white ppv2-rounded-2xl ppv2-shadow-xl ppv2-p-6">
                <div class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-mb-2">
                    <div
                        class="ppv2-w-10 ppv2-h-10 ppv2-rounded-full ppv2-bg-red-50 ppv2-flex ppv2-items-center ppv2-justify-center"
                        style="color: #e2483d"
                    >
                        <FormTypeIcon name="trash" :size="18" />
                    </div>
                    <span class="poppinsSemiBold ppv2-text-gray-900" :style="{ fontSize: normalizeFontSize(16) }">
                        {{ $t('forms.deleteFormTitle') }}
                    </span>
                </div>
                <p class="poppinsRegular ppv2-text-gray-500 ppv2-mt-2" :style="{ fontSize: normalizeFontSize(13) }">
                    {{ $t('forms.deleteFormMessage') }}
                </p>
                <div class="ppv2-flex ppv2-justify-end ppv2-gap-2 ppv2-mt-5">
                    <BaseButton
                        type="button"
                        variant="outline"
                        :custom-class="'!ppv2-min-h-[38px] !ppv2-min-w-[100px] !ppv2-text-sm'"
                        @click="deleteCandidateSlug = null"
                    >
                        {{ $t('forms.question.cancel') }}
                    </BaseButton>
                    <BaseButton
                        type="button"
                        variant="danger"
                        :custom-class="'!ppv2-min-h-[38px] !ppv2-min-w-[120px] !ppv2-text-sm'"
                        :disabled="isDeleting"
                        @click="confirmDeleteForm"
                    >
                        {{ $t('forms.question.delete') }}
                    </BaseButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import normalizeSize from '../../../utils/normalizeSize'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import Header from '../../Shared/Header.vue'
import FormBuilder from './FormBuilder.vue'
import FormTypeIcon from './components/FormTypeIcon.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import { MoreHorizontal } from 'lucide-vue'

const FORM_DEFS = ['demande-entreprise', 'demande-anniversaire', 'demande-evg']

export default {
    name: 'FormQuestionIndex',
    components: {
        Header,
        FormBuilder,
        FormTypeIcon,
        BaseSwitch,
        BaseButton,
        MoreHorizontal,
    },
    props: {
        userId: {
            type: Number,
            default: null,
        },
        titleAnv: {
            type: String,
            default: '',
        },
        titleEvg: {
            type: String,
            default: '',
        },
        titleEnt: {
            type: String,
            default: '',
        },
        canCreateDemandeDevis: {
            type: Boolean,
            default: false,
        },
        canConsultDemandeDevis: {
            type: Boolean,
            default: false,
        },
        canModifyDemandeDevis: {
            type: Boolean,
            default: false,
        },
        canDeleteDemandeDevis: {
            type: Boolean,
            default: false,
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            maxForms: 3,
            activeSlug: 'demande-entreprise',
            renamingSlug: null,
            renameInput: '',
            menuSlug: null,
            deleteCandidateSlug: null,
            isDeleting: false,
            forms: {},
            active: {},
        }
    },
    computed: {
        tabs() {
            return FORM_DEFS.filter((def) => this.forms[def]).map((slug) => ({
                slug,
                fallback: this.fallbackName(slug),
            }))
        },
        activeForm() {
            return this.forms[this.activeSlug] || {}
        },
        activeFallbackName() {
            const tab = this.tabs.find((t) => t.slug === this.activeSlug)
            return tab ? tab.fallback : ''
        },
        formCount() {
            return Object.keys(this.forms).length
        },
    },
    mounted() {
        this.loadForms()
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        fallbackName(slug) {
            if (slug === 'demande-anniversaire') return this.titleAnv || this.$t('form_question.tabs.anniversaire')
            if (slug === 'demande-evg') return this.titleEvg || this.$t('form_question.tabs.evg')
            return this.titleEnt || this.$t('form_question.tabs.entreprise')
        },
        formMeta(slug) {
            return this.forms[slug]
        },
        selectTab(slug) {
            this.menuSlug = null
            this.activeSlug = slug
        },
        menuClick(slug) {
            const wasOpen = this.menuSlug === slug
            this.selectTab(slug)
            this.menuSlug = wasOpen ? null : slug
        },
        isActive(slug) {
            return !!this.active[slug]
        },
        async toggleActive(slug) {
            this.menuSlug = null
            const previous = this.active[slug]
            const newValue = !previous
            this.active[slug] = newValue
            try {
                const response = await axios.post(
                    '/api/v3/formQuestion/toggleActive',
                    { slug, is_active: newValue },
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                if (!response.data.success) {
                    this.active[slug] = previous
                    this.$toast.error(response.data.message || 'Erreur lors de la modification')
                }
            } catch (error) {
                this.active[slug] = previous
                this.$toast.error(error.response?.data?.message || error.message)
            }
        },
        handleClickOutside(event) {
            if (this.$refs.tabMenuArea && !this.$refs.tabMenuArea.contains(event.target)) {
                this.menuSlug = null
            }
        },
        async loadForms() {
            try {
                const response = await axios.get('/api/v3/formQuestion/list', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                if (!response.data.success) {
                    this.$toast.error(response.data.message || this.$t('forms.formListError'))
                    return
                }
                const forms = {}
                const active = {}
                const surveys = response.data.data || []
                surveys.forEach((survey) => {
                    forms[survey.slug] = {
                        name: survey.name || '',
                        count: survey.question_count || 0,
                        loaded: true,
                    }
                    active[survey.slug] = survey.is_active === true || survey.is_active === 1
                })
                this.forms = forms
                this.active = active
                if (!this.forms[this.activeSlug]) {
                    const remaining = this.tabs
                    this.activeSlug = remaining.length ? remaining[0].slug : ''
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || error.message)
            }
        },
        async fetchForm(slug) {
            const form = this.forms[slug]
            if (!form) return
            form.loading = true
            try {
                const response = await axios.get('/api/v3/formQuestion/getForm', {
                    params: { type: slug },
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                if (response.data.success) {
                    const survey = response.data.data.survey
                    const tab = this.tabs.find((t) => t.slug === slug)
                    if (!form.isNew) form.name = survey.name || (tab && tab.fallback)
                    form.count = (survey.questions || []).length
                    this.active[slug] = survey.is_active === true || survey.is_active === 1
                    form.loaded = true
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    this.$delete(this.forms, slug)
                    this.$delete(this.active, slug)
                    if (this.activeSlug === slug) {
                        const remaining = this.tabs
                        this.activeSlug = remaining.length ? remaining[0].slug : ''
                    }
                    return
                }
                this.$toast.error(error.response?.data?.message || error.message)
            } finally {
                form.loading = false
            }
        },
        async addForm() {
            if (this.formCount >= this.maxForms) return
            this.menuSlug = null
            try {
                const response = await axios.post(
                    '/api/v3/formQuestion/create',
                    {},
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                if (!response.data.success) {
                    this.$toast.error(response.data.message || this.$t('forms.formCreateError'))
                    return
                }
                const survey = response.data.data
                if (!survey || !survey.slug) {
                    this.$toast.error(this.$t('forms.formCreateError'))
                    return
                }
                this.$set(this.forms, survey.slug, {
                    name: survey.name || this.$t('forms.addNew'),
                    count: 0,
                    loaded: false,
                    isNew: true,
                })
                this.$set(this.active, survey.slug, survey.is_active === true || survey.is_active === 1)
                this.activeSlug = survey.slug
                await this.fetchForm(survey.slug)
                this.startRename(survey.slug)
            } catch (error) {
                this.$toast.error(error.response?.data?.message || error.message || this.$t('forms.formCreateError'))
            }
        },
        requestDeleteForm(slug) {
            this.menuSlug = null
            this.deleteCandidateSlug = slug
        },
        async duplicateForm(slug) {
            if (this.formCount >= this.maxForms) return
            this.menuSlug = null
            try {
                const response = await axios.post(
                    '/api/v3/formQuestion/duplicate',
                    { slug },
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                if (!response.data.success) {
                    this.$toast.error(response.data.message || this.$t('forms.formDuplicateError'))
                    return
                }
                const newSlug = response.data.data && response.data.data.slug
                if (!newSlug) {
                    this.$toast.error(this.$t('forms.formDuplicateError'))
                    return
                }
                this.$set(this.forms, newSlug, { name: '', count: 0, loaded: false })
                this.$set(this.active, newSlug, true)
                this.activeSlug = newSlug
                await this.fetchForm(newSlug)
                this.$toast.success(response.data.message || this.$t('forms.formDuplicated'))
            } catch (error) {
                this.$toast.error(error.response?.data?.message || error.message || this.$t('forms.formDuplicateError'))
            }
        },
        confirmDeleteForm() {
            const slug = this.deleteCandidateSlug
            if (!slug) return
            if (this.formCount <= 1) {
                this.deleteCandidateSlug = null
                return
            }
            this.isDeleting = true
            axios
                .post('/api/v3/formQuestion/delete', { slug }, { headers: { Authorization: `Bearer ${this.token}` } })
                .then((response) => {
                    if (!response.data.success) {
                        throw new Error(response.data.message || this.$t('forms.formDeleteError'))
                    }
                    this.$delete(this.forms, slug)
                    this.$delete(this.active, slug)
                    this.deleteCandidateSlug = null
                    if (this.activeSlug === slug) {
                        const remaining = this.tabs
                        this.activeSlug = remaining.length ? remaining[0].slug : ''
                    }
                    this.$toast.success(response.data.message || this.$t('forms.formDeleted'))
                })
                .catch((error) => {
                    this.$toast.error(error.response?.data?.message || error.message || this.$t('forms.formDeleteError'))
                })
                .finally(() => {
                    this.isDeleting = false
                    this.deleteCandidateSlug = null
                })
        },
        startRename(slug) {
            this.menuSlug = null
            this.renamingSlug = slug
            this.renameInput = this.forms[slug].name || ''
            this.$nextTick(() => {
                if (this.$refs.renameInput) this.$refs.renameInput[0].focus()
            })
        },
        commitRename() {
            const slug = this.renamingSlug
            if (!slug) return
            const trimmed = (this.renameInput || '').trim()
            const form = this.forms[slug]
            if (!form) return
            form.name = trimmed || (form.isNew ? this.$t('forms.addNew') : form.name)
            this.renamingSlug = null
        },
        onSaved({ name, count }) {
            const form = this.forms[this.activeSlug]
            if (!form) return
            if (name) form.name = name
            form.count = count
            form.isNew = false
        },
        onCountChange(count) {
            const form = this.forms[this.activeSlug]
            if (form) form.count = count
        },
    },
}
</script>
